import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Pencil, Plus, Power, Trash2, X } from 'lucide-react'
import {
  fetchPolicies, createPolicy, updatePolicy, deletePolicy, fetchFirms, Policy, PolicyCreate, FirmSummary,
} from '../../lib/api/api'
import { useAuth } from '../../contexts/AuthContext'
import { formatDateTime } from '../../shared/utils/format'

type FormState = { title: string; description: string; category: string; content: string; priority: number }
const emptyForm: FormState = { title: '', description: '', category: '', content: '', priority: 0 }

const toForm = (policy: Policy): FormState => ({
  title: policy.title,
  description: policy.description || '',
  category: policy.category || '',
  content: policy.content,
  priority: policy.priority,
})

export default function PoliciesPage() {
  const { t } = useTranslation()
  const { user, isSuperAdmin, canManageFirm } = useAuth()
  // The super admin belongs to no firm: they need to pick one before the
  // list means anything (a member's own firm is implicit everywhere else).
  const needsFirmPicker = isSuperAdmin && !user?.firm

  const [firms, setFirms] = useState<FirmSummary[]>([])
  const [selectedFirmId, setSelectedFirmId] = useState('')

  const [policies, setPolicies] = useState<Policy[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (!needsFirmPicker) return
    fetchFirms().then((res) => setFirms(res.data.firms)).catch(() => {})
  }, [needsFirmPicker])

  useEffect(() => {
    let active = true
    if (needsFirmPicker && !selectedFirmId) {
      setPolicies([])
      return
    }
    setIsLoading(true)
    setError(null)
    const params = needsFirmPicker ? { firm_id: selectedFirmId } : {}
    fetchPolicies(canManageFirm, params)
      .then((res) => { if (active) setPolicies(res.data) })
      .catch(() => { if (active) setError(t('policies.errors.loadFailed')) })
      .finally(() => { if (active) setIsLoading(false) })
    return () => { active = false }
  }, [needsFirmPicker, selectedFirmId, canManageFirm, reloadToken, t])

  const canManageList = canManageFirm && !(needsFirmPicker && !selectedFirmId)

  const openCreate = () => {
    setForm(emptyForm)
    setEditingPolicy(null)
    setIsFormOpen(true)
  }

  const openEdit = (policy: Policy) => {
    setForm(toForm(policy))
    setEditingPolicy(policy)
    setIsFormOpen(true)
  }

  const closeForm = () => {
    setIsFormOpen(false)
    setEditingPolicy(null)
    setForm(emptyForm)
  }

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.content.trim()) return
    setIsSaving(true)
    setError(null)
    try {
      if (editingPolicy) {
        await updatePolicy(editingPolicy.id, {
          title: form.title, description: form.description, category: form.category,
          content: form.content, priority: form.priority,
        })
      } else {
        const body: PolicyCreate = {
          title: form.title, description: form.description, category: form.category,
          content: form.content, priority: form.priority,
        }
        if (needsFirmPicker) body.firm_id = selectedFirmId
        await createPolicy(body)
      }
      closeForm()
      setReloadToken((v) => v + 1)
    } catch {
      setError(t('policies.errors.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggleActive = async (policy: Policy) => {
    try {
      await updatePolicy(policy.id, { is_active: !policy.is_active })
      setReloadToken((v) => v + 1)
    } catch {
      setError(t('policies.errors.toggleFailed'))
    }
  }

  const handleDelete = async (policy: Policy) => {
    if (!window.confirm(t('policies.list.confirmDelete', { title: policy.title }))) return
    try {
      await deletePolicy(policy.id)
      setPolicies((prev) => prev.filter((p) => p.id !== policy.id))
    } catch {
      setError(t('policies.errors.deleteFailed'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-gray-100">{t('policies.title')}</h1>
        {canManageList && (
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm"
          >
            <Plus size={16} />
            {t('policies.new')}
          </button>
        )}
      </div>

      {needsFirmPicker && (
        <div className="flex items-center gap-2">
          <label htmlFor="policies-firm-picker" className="text-sm text-gray-600 dark:text-gray-400">
            {t('policies.firmPicker.label')}
          </label>
          <select
            id="policies-firm-picker"
            value={selectedFirmId}
            onChange={(e) => setSelectedFirmId(e.target.value)}
            className="border border-gray-300 dark:border-gray-700 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
          >
            <option value="">{t('policies.firmPicker.placeholder')}</option>
            {firms.map((firm) => (
              <option key={firm.id} value={firm.id}>{firm.name}</option>
            ))}
          </select>
        </div>
      )}

      {error && <p className="text-red-600 text-sm">{error}</p>}

      {needsFirmPicker && !selectedFirmId ? (
        <p className="text-gray-500 text-sm">{t('policies.firmPicker.empty')}</p>
      ) : isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-gray-400" />
        </div>
      ) : policies.length === 0 ? (
        <p className="text-gray-500 text-center py-8">{t('policies.list.noResults')}</p>
      ) : (
        <div className="space-y-2">
          {policies.map((policy) => (
            <div key={policy.id} className="p-4 border rounded-lg bg-white dark:bg-gray-800">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">{policy.title}</h3>
                    {policy.category && (
                      <span className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">
                        {policy.category}
                      </span>
                    )}
                    {!policy.is_active && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {t('policies.list.inactive')}
                      </span>
                    )}
                  </div>
                  {policy.description && <p className="text-sm text-gray-600 mt-1">{policy.description}</p>}
                  <p className="text-sm text-gray-500 mt-2 whitespace-pre-wrap">{policy.content}</p>
                  <p className="text-xs text-gray-400 mt-2">{formatDateTime(policy.updated_at || policy.created_at)}</p>
                </div>
                {canManageList && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => openEdit(policy)}
                      title={t('common.edit')}
                      aria-label={t('common.edit')}
                      className="p-2 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleActive(policy)}
                      title={policy.is_active ? t('policies.actions.deactivate') : t('policies.actions.activate')}
                      aria-label={policy.is_active ? t('policies.actions.deactivate') : t('policies.actions.activate')}
                      className="p-2 rounded-full text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                    >
                      <Power size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(policy)}
                      title={t('common.delete')}
                      aria-label={t('common.delete')}
                      className="p-2 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {isFormOpen && canManageList && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">
                {editingPolicy ? t('policies.form.editTitle') : t('policies.form.createTitle')}
              </h3>
              <button type="button" onClick={closeForm} className="text-gray-500 hover:text-gray-700">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              <label className="block text-sm">
                <span className="text-gray-600">{t('policies.form.titleLabel')}</span>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-600">{t('policies.form.descriptionLabel')}</span>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="mt-1 w-full border rounded-md px-3 py-1.5"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-gray-600">{t('policies.form.categoryLabel')}</span>
                  <input
                    type="text"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="mt-1 w-full border rounded-md px-3 py-1.5"
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-600">{t('policies.form.priorityLabel')}</span>
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value, 10) || 0 })}
                    min={0}
                    max={100}
                    className="mt-1 w-full border rounded-md px-3 py-1.5"
                  />
                </label>
              </div>
              <label className="block text-sm">
                <span className="text-gray-600">{t('policies.form.contentLabel')}</span>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={8}
                  className="mt-1 w-full border rounded-md px-3 py-1.5 font-mono text-sm"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t">
              <button type="button" onClick={closeForm} className="px-3 py-1.5 rounded-md border text-sm">
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSaving}
                className="px-3 py-1.5 rounded-md bg-primary-600 text-white text-sm disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
